import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FileSpreadsheet, UploadCloud } from 'lucide-react';
import Badge from './Badge.jsx';
import Button from './Button.jsx';
import Card from './Card.jsx';

function parseAccept(accept) {
  if (typeof accept !== 'string') return [];
  return accept
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
}

function matchesAccept(file, tokens) {
  if (tokens.length === 0) return true;
  const name = (file.name ?? '').toLowerCase();
  const mime = (file.type ?? '').toLowerCase();
  return tokens.some((token) => {
    if (token.startsWith('.')) return name.endsWith(token);
    if (token.endsWith('/*')) return mime.startsWith(token.slice(0, -1));
    return mime === token;
  });
}

function extensionOf(name = '') {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toUpperCase() : '';
}

/**
 * Drag & drop file picker with type, size and single-file validation. Controlled and
 * presentational: it never reads or parses the file content. Renders the drop zone, or a
 * loaded-file card (name, summary, type badge, Replace action) when `file` is provided.
 *
 * Validation order on drop or pick: `multiple`, then `type`, then `size`. An invalid file is
 * never passed to `onFileSelected`; `onError({ code, file? })` fires instead.
 * Keyboard: the zone is focusable; Enter/Space opens the picker.
 *
 * @param {object} props
 * @param {string} [props.accept] — Comma-separated extensions / MIME types (e.g. `".xlsx,.csv"`).
 * @param {number} [props.maxSizeBytes] — Max file size in bytes.
 * @param {string} props.title — Drop zone heading.
 * @param {string} [props.description] — Helper text (accepted formats, limits).
 * @param {string} props.chooseLabel — Label of the "Choose file" button.
 * @param {string} [props.replaceLabel='Replace'] — Label of the Replace button in the loaded card.
 * @param {{ name: string, summary?: string }} [props.file] — Loaded file; switches to the card state.
 * @param {(file: File) => void} [props.onFileSelected]
 * @param {(error: { code: 'type'|'size'|'multiple', file?: File }) => void} [props.onError]
 * @param {() => void} [props.onReplace] — Called when Replace is clicked, right before the picker opens.
 * @param {boolean} [props.disabled]
 * @param {import('lucide-react').LucideIcon} [props.icon] — Icon of the loaded-file card (default: spreadsheet).
 * @param {string} [props.className]
 */
const FileDropzone = ({
  accept,
  maxSizeBytes,
  title,
  description,
  chooseLabel,
  replaceLabel = 'Replace',
  file,
  onFileSelected,
  onError,
  onReplace,
  disabled = false,
  icon: FileIcon = FileSpreadsheet,
  className = '',
}) => {
  const inputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const [dragActive, setDragActive] = useState(false);

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleFiles = (fileList) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;
    if (files.length > 1) {
      onError?.({ code: 'multiple' });
      return;
    }
    const [candidate] = files;
    if (!matchesAccept(candidate, parseAccept(accept))) {
      onError?.({ code: 'type', file: candidate });
      return;
    }
    if (typeof maxSizeBytes === 'number' && candidate.size > maxSizeBytes) {
      onError?.({ code: 'size', file: candidate });
      return;
    }
    onFileSelected?.(candidate);
  };

  const handleInputChange = (event) => {
    handleFiles(event.target.files);
    // Allow picking the same file again after a Replace or a rejected pick.
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    dragDepthRef.current = 0;
    setDragActive(false);
    if (disabled) return;
    handleFiles(event.dataTransfer?.files);
  };

  // Enter/leave fire for every child element crossed (and `relatedTarget` is null in Safari),
  // so a depth counter, not the event target, decides when the drag really left the zone.
  const handleDragEnter = (event) => {
    event.preventDefault();
    dragDepthRef.current += 1;
    if (!disabled) setDragActive(true);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!disabled) setDragActive(true);
  };

  const handleDragLeave = () => {
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  };

  // Loaded-file card: swallow the drop so the browser does not open the file in the tab.
  const preventDefault = (event) => event.preventDefault();

  const handleKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPicker();
    }
  };

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      className="sr-only"
      tabIndex={-1}
      aria-hidden="true"
      disabled={disabled}
      onClick={(event) => event.stopPropagation()}
      onChange={handleInputChange}
    />
  );

  // The hidden input is rendered once, outside both branches, so it survives the switch
  // between the drop zone and the loaded-file card (e.g. `onReplace` clearing `file`).
  let content;
  if (file) {
    const ext = extensionOf(file.name);
    content = (
      <Card
        onDragOver={preventDefault}
        onDrop={preventDefault}
        className={['flex items-center gap-4 px-5 py-4', className].filter(Boolean).join(' ')}
      >
        <div
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-content-secondary"
        >
          <FileIcon size={20} strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-content">{file.name}</span>
            {ext ? (
              <Badge size="small" variant="neutral">
                {ext}
              </Badge>
            ) : null}
          </div>
          {file.summary ? (
            <p className="mt-0.5 text-xs text-content-secondary">{file.summary}</p>
          ) : null}
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={disabled}
          onClick={() => {
            onReplace?.();
            openPicker();
          }}
        >
          {replaceLabel}
        </Button>
      </Card>
    );
  } else {
    content = (
      <div
        role="group"
        aria-label={title}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        data-drag-active={dragActive ? 'true' : 'false'}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={[
          'flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 text-center',
          'transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#80AFFF] focus-visible:ring-offset-2',
          dragActive
            ? 'border-[#2173FF] bg-[#2173FF]/10'
            : 'border-edge-strong bg-surface-muted/40',
          !dragActive && !disabled ? 'hover:bg-surface-muted/70' : '',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={openPicker}
      >
        <UploadCloud size={28} strokeWidth={1.6} className="text-content-secondary" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold text-content">{title}</p>
          {description ? <p className="mt-1 text-xs text-content-secondary">{description}</p> : null}
        </div>
        {/* The zone is the single tab stop; the button stays clickable with the mouse. */}
        <Button variant="secondary" size="sm" disabled={disabled} tabIndex={-1}>
          {chooseLabel}
        </Button>
      </div>
    );
  }

  return (
    <>
      {content}
      {hiddenInput}
    </>
  );
};

FileDropzone.propTypes = {
  accept: PropTypes.string,
  maxSizeBytes: PropTypes.number,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  chooseLabel: PropTypes.string.isRequired,
  replaceLabel: PropTypes.string,
  file: PropTypes.shape({ name: PropTypes.string.isRequired, summary: PropTypes.string }),
  onFileSelected: PropTypes.func,
  onError: PropTypes.func,
  onReplace: PropTypes.func,
  disabled: PropTypes.bool,
  icon: PropTypes.elementType,
  className: PropTypes.string,
};

export default FileDropzone;
